// Co-managers, in Edit team (DIRECTION A14, scenario B13,
// PLAN-2026-10-18-families-and-newcomers §5.4): who runs the team, a link to
// invite one more, and a co-manager off it.
//
// WHO SEES WHAT. Everybody who opens it reads who runs the team. The team's
// manager and a commissioner get **Invite a co-manager**, the link once made,
// and **Remove** beside each co-manager; a co-manager gets **Step down**.
//
// THE LINK SAYS WHAT IT DOES before it is sent (CLAUDE.md, "say what will
// happen"): it is for one person, it lets them do everything the manager does
// but invite others or give the team away, and it stops working once used.
// A real league's only: the practice league has no co-managers.

import { useEffect, useState } from 'react';
import type { TeamManagersView } from '@fsp/domain';
import { Button, Caption, Line, Stack, Text, TextField, tell } from '../../kit/ui';
import { messageOf } from '../../data/api';
import {
  cancelCoManagerInvite,
  coManagerLink,
  inviteCoManager,
  removeCoManager,
  teamManagers,
} from '../../data/coManagers';
import { Confirm } from './Confirm';

export function CoManagers({
  leagueId,
  teamId,
  onLeft,
}: {
  leagueId: string;
  teamId: string;
  /** The reader stepped down: the team is no longer theirs to edit. */
  onLeft: () => void;
}) {
  const [view, setView] = useState<TeamManagersView | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    void teamManagers(leagueId, teamId).then((reply) => {
      if (!live) return;
      if (reply.status === 'ok') setView(reply.data);
      else setProblem(messageOf(reply, 'Who runs this team could not be read.'));
    });
    return () => {
      live = false;
    };
  }, [leagueId, teamId]);

  const act = async (
    run: () => Promise<Awaited<ReturnType<typeof teamManagers>>>,
    done?: string,
  ) => {
    setBusy(true);
    const reply = await run();
    setBusy(false);
    if (reply.status !== 'ok') {
      tell(messageOf(reply, 'That did not work. Nothing changed.'), 'bad');
      return null;
    }
    setView(reply.data);
    if (done) tell(done, 'good');
    return reply.data;
  };

  if (problem !== null) return <Text tone="warn">{problem}</Text>;
  if (view === null) return null;

  const link = view.invite === null ? null : coManagerLink(view.invite.code);
  const full = view.coManagers.length >= view.max;
  const share = () => {
    if (link === null) return;
    const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
    if (typeof nav.share === 'function') {
      // A dismissed share sheet rejects; that is the person changing their mind.
      void nav.share({ title: `Help run ${view.teamName}`, url: link }).catch(() => undefined);
      return;
    }
    if (!navigator.clipboard) {
      tell('Copy the link above: this browser will not copy it for you.', 'warn');
      return;
    }
    void navigator.clipboard.writeText(link).then(
      () => tell('Link copied.', 'good'),
      () => tell('Copy the link above: this browser will not copy it for you.', 'warn'),
    );
  };

  return (
    <section aria-labelledby="fsp-co-managers-title">
      <Stack gap={3}>
        <div>
          <div className="fsp-field-label" id="fsp-co-managers-title">
            Who runs it
          </div>
          <Text size="sm" tone="secondary" block>
            Up to {view.max} co-managers can help run a team: set the lineup, make moves, trade and
            draft, as the manager does.
          </Text>
        </div>
        <Stack gap={2}>
          <Line gap={2} spread>
            <Text>{view.manager ?? 'Nobody yet'}</Text>
            <Caption>Manager</Caption>
          </Line>
          {view.coManagers.map((c) => (
            <Line key={c.id} gap={2} spread>
              <Text>{c.isMe ? `${c.name} (you)` : c.name}</Text>
              {c.isMe ? (
                <Confirm
                  label="Step down"
                  confirmLabel="Leave the team and the league?"
                  tone="warn"
                  busy={busy}
                  onConfirm={() =>
                    void act(
                      () => removeCoManager(leagueId, teamId, c.id),
                      'You stepped down.',
                    ).then((left) => left !== null && onLeft())
                  }
                />
              ) : view.canManage ? (
                <Confirm
                  label="Remove"
                  confirmLabel={`Remove ${c.name} from the team and the league?`}
                  busy={busy}
                  onConfirm={() =>
                    void act(
                      () => removeCoManager(leagueId, teamId, c.id),
                      `${c.name} was removed.`,
                    )
                  }
                />
              ) : (
                <Caption>Co-manager</Caption>
              )}
            </Line>
          ))}
        </Stack>

        {view.canManage && view.manager !== null ? (
          link !== null ? (
            <Stack gap={2}>
              <Text size="sm" block>
                Send this link to one person. It lets them set the lineup, make moves, trade and
                draft for the team; inviting others, the team’s votes and giving the team away stay
                the manager’s. It stops working once they use it.
              </Text>
              <TextField
                label="Co-manager link"
                hideLabel
                readOnly
                value={link}
                onChange={() => {}}
              />
              <Line gap={2} wrap>
                <Button size="xs" variant="primary" icon="share" onClick={share}>
                  Share the link
                </Button>
                <Button
                  size="xs"
                  variant="quiet"
                  disabled={busy}
                  onClick={() =>
                    void act(
                      () => cancelCoManagerInvite(leagueId, teamId),
                      'The link no longer works.',
                    )
                  }
                >
                  Cancel the link
                </Button>
              </Line>
            </Stack>
          ) : full ? (
            <Text size="sm" tone="secondary" block>
              This team has all {view.max} co-managers it can have.
            </Text>
          ) : (
            <div>
              <Button
                size="xs"
                icon="invite"
                disabled={busy}
                onClick={() => void act(() => inviteCoManager(leagueId, teamId))}
              >
                Invite a co-manager
              </Button>
            </div>
          )
        ) : null}
      </Stack>
    </section>
  );
}
